import React from 'react';
import { Card } from "../../../components/tailwind/card";
import { Badge } from "../../../components/tailwind/badge";
import { CheckCircle2, Clock, XCircle, Ban, Send, Loader2 } from "lucide-react";
import { CampaignActions } from "./CampaignActions";

const STATUS_CONFIGS = {
  completed: {
    label: "Completed",
    icon: CheckCircle2,
    colorClass: "text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 dark:text-emerald-400"
  },
  running: {
    label: "Running",
    icon: Loader2,
    colorClass: "text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400",
    animate: true
  },
  scheduled: {
    label: "Scheduled",
    icon: Clock,
    colorClass: "text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400"
  },
  published: {
    label: "Published",
    icon: Send,
    colorClass: "text-violet-600 bg-violet-50 dark:bg-violet-900/20 dark:text-violet-400"
  },
  cancelled: {
    label: "Cancelled",
    icon: Ban,
    colorClass: "text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400"
  },
  failed: {
    label: "Failed",
    icon: XCircle,
    colorClass: "text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400"
  }
};

// Helper for status badge
const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  const config = STATUS_CONFIGS[s] || STATUS_CONFIGS.failed;
  const Icon = config.icon;
  
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.colorClass}`}>
      <Icon className={`h-3.5 w-3.5 ${config.animate ? "animate-spin" : ""}`} />
      {config.label}
    </div>
  );
};

// Format date-time helper
const formatDateTime = (date) => {
  if (!date) return '-';
  try {
    const d = typeof date === 'number' ? new Date(date) : new Date(date);
    if (isNaN(d.getTime())) return date;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ', ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch {
    return date;
  }
};

export const CampaignTable = ({ campaigns, onRowClick, onReschedule, onCancel, onView }) => {
  if (!campaigns || campaigns.length === 0) {
    return (
      <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-12">
        <div className="text-center text-slate-500 dark:text-slate-400">
          <p className="text-lg font-medium mb-1">No campaigns found</p>
          <p className="text-sm">Create your first campaign to get started</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-950 border-b border-slate-100 dark:border-slate-800">
              <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Campaign</th>
              <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
              <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Created At</th>
              <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Scheduled For</th>
              <th className="text-center p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
              <th className="text-right p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {campaigns.map((campaign) => {
              const createdAt = campaign.created_at;
              const scheduledFor = campaign.scheduled_at;

              return (
                <tr 
                  key={campaign.campaign_id} 
                  className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors group cursor-pointer"
                  onClick={() => onRowClick?.(campaign)}
                >
                  <td className="p-4">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white group-hover:text-blue-600 transition-colors">
                        {campaign.name}
                      </p>
                    </div>
                  </td>
                  <td className="p-4">
                    <Badge variant="secondary" className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 font-normal">
                      {campaign.category || campaign.channel || 'Broadcast'}
                    </Badge>
                  </td>
                  <td className="p-4 text-left text-sm text-slate-600 dark:text-slate-400">
                    {formatDateTime(createdAt)}
                  </td>
                  <td className="p-4 text-left text-sm text-slate-600 dark:text-slate-400">
                    {formatDateTime(scheduledFor)}
                  </td>
                  <td className="p-4 text-center">
                    <StatusBadge status={campaign.status} />
                  </td>
                  <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                    <CampaignActions
                      campaign={campaign}
                      onReschedule={onReschedule}
                      onCancel={onCancel}
                      onView={onView}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};